import Honeybadger from "$app/utils/honeybadger"
import { xhr, toast } from "$app/utils"
import { appStore } from "$app/store.svelte"

import strengthImg from "@images/onboarding/strengths.png"
import weaknessImg from "@images/onboarding/weaknesses.png"
import opportunityImg from "@images/onboarding/opportunities.png"
import threatImg from "@images/onboarding/threats.png"

class OnboardingStore {
  current_onboarding_step = $state<string>("")
  onboarding_steps = $state<string[]>([])

  // This is used in many places like in StatementForm.svelte and the PrevNextButtons.svelte check this state to enable continue button
  saving = $state(false)

  // Indicates the state of completed this works especially when there is a list of steps that needs to be completed like in Values
  completed = $state(false)
  errors = $state({} as { [key: string]: any })

  get completed_onboarding_step() {
    try {
      const _step = this.onboarding_steps.find((el) => !appStore.plan.onboarding_steps[el])

      if (!_step) throw "Error"

      return _step
    } catch (e) {
      return this.onboarding_steps[0]
    }
  }

  get prevStepName() {
    const currentStep = this.current_onboarding_step
    const onboardSteps = this.onboarding_steps

    let currentIndex = onboardSteps.findIndex((el) => el === currentStep)

    if (currentIndex >= 1) {
      return onboardSteps[currentIndex - 1]
    } else {
      return onboardSteps[0]
    }
  }

  get nextStepName() {
    const currentStep = this.current_onboarding_step
    const onboardSteps = this.onboarding_steps

    const currIndex = onboardSteps.findIndex((el) => el === currentStep)

    if (currIndex < onboardSteps.length) {
      return onboardSteps[currIndex + 1]
    } else {
      return onboardSteps[onboardSteps.length]
    }
  }

  nextStep = async (): Promise<void> => {
    this.saving = true
    this.errors = {}

    const nextStep = this.nextStepName

    try {
      const resp = await xhr.patch(`/plan_onboarding/${appStore.plan.id}.json}`, {
        plan: { onboarding_step: this.current_onboarding_step }
      })

      appStore.plan = { ...appStore.plan, ...resp.data.plan }

      this.current_onboarding_step = nextStep
    } catch (e) {
      console.log(e)
      if (e.response.data) {
        this.errors = e.response.data
      } else {
        if (typeof Honeybadger.notify !== "undefined") {
          Honeybadger.notify(e)
        }
      }
      toast("There was an error saving your plan data", "error")
    } finally {
      this.saving = false
    }
  }

  prevStep = async (): Promise<void> => {
    this.saving = true

    const prevStep = this.prevStepName
    try {
      this.current_onboarding_step = prevStep
    } catch (e) {
      if (typeof Honeybadger.notify !== "undefined") {
        Honeybadger.notify(e)
      }
      toast("There was an error moving to the previous step", "error")
    } finally {
      this.saving = false
    }
  }

  skipOnboarding = async (): Promise<void> => {
    this.saving = true

    try {
      await xhr.patch(`/plan_onboarding/${appStore.plan.id}/skip_onboarding.json`, {
        skip_onboarding: true
      })
      window.location.href = `/plans/${appStore.plan.id}`
    } catch (e) {
      toast("There was an error skipping onboarding", "error")
    } finally {
      this.saving = false
    }
  }

  setBrowserHistory = (data: any, step: string = ""): void => {
    if (!step) step = this.current_onboarding_step

    setTimeout(() => {
      window.history.pushState(data, step, `?step=${step}`)
    }, 300)
  }

  valuesOptions = [
    {
      name: "Caring",
      description: "Caring is the act or pursuit of putting others first."
    },
    {
      name: "Innovative",
      description:
        "We never rest knowing that we work in a face-paced, ever-changing society. We function to make sound decisions while being agile when change is needed in the moment. We utilize visionary thinking as a tool to continue to create better."
    },
    {
      name: "Integrity",
      description:
        "Integrity is doing what is right even when no one is watching. A company that embodies integrity can trust one another and, as a result, create a sense of belonging and reliability for their employees and clients. We are transparent, honest, forgiving, humble, coachable, and believe the best about others."
    },
    {
      name: "Strive for Excellence",
      description:
        "We hold ourselves to the highest level standard. We will allow ourself and others to hold us accountable and continue to improve in all areas to be the best we can."
    },
    {
      name: "Teamwork",
      description:
        "We achieve results through collaboration. While debating our differences, we fully support and execute our decision."
    },
    {
      name: "Inclusive",
      description:
        "We aim to include every employee so they are empowered to have a voice. Through creating an inclusive environment, people feel valued, respected, and supported. "
    },
    {
      name: "Grit",
      description:
        "We aim to include every employee so they are empowered to have a voice. Through creating an inclusive environment, people feel valued, respected, and supported. "
    },
    {
      name: "Embrace Change",
      description:
        "We aim to include every employee so they are empowered to have a voice. Through creating an inclusive environment, people feel valued, respected, and supported. "
    },
    {
      name: "Passionate",
      description:
        "We aim to include every employee so they are empowered to have a voice. Through creating an inclusive environment, people feel valued, respected, and supported. "
    },
    {
      name: "Empowerment",
      description:
        "We want to empower others to have a view, a voice that will be heard, and an opinion that is valued. Through mentorship we encourage our employees to empower their teammates and our clients."
    },
    {
      name: "Creativity",
      description:
        "We want to empower others to have a view, a voice that will be heard, and an opinion that is valued. Through mentorship we encourage our employees to empower their teammates and our clients."
    },
    {
      name: "Collaboration",
      description:
        "We believe in the power of working together, going above and beyond to solve challenges and find the best solution. "
    },
    {
      name: "Golden Rule",
      description:
        "We believe in the power of working together, going above and beyond to solve challenges and find the best solution. "
    },
    {
      name: "Heart",
      description:
        "We treat our team with warmth, care, and appreciation that they deserve. "
    },
    {
      name: "Thought",
      description:
        "We take the time to plan ahead, practice our craft, and develop our professional skill sets. We practice thought when we interact with other employees and customers."
    },
    {
      name: "Health",
      description:
        "We invest in practices and mindsets that keep our team safe, productive, and as motivated as possible. Our success depends on the wellbeing and satisfaction of everyone on our team."
    },
    {
      name: "Quality",
      description:
        "Quality is the ability to fulfill high standards and exceed client expectations. Everything we do is done with the highest accuracy."
    },
    {
      name: "Knowledgeable",
      description:
        "We value continuous learning and development to stay at the forefront of our industry as well as provide the best service and solutions to our clients. "
    },
    {
      name: "Consistency",
      description:
        "We strive for consistency in our actions and decisions, building trust and dependability with our team and clients. "
    },
    {
      name: "Fun",
      description:
        "We enjoy our work, have an enjoyable work environment, and value having fun in what we do. "
    }
  ]

  swotOptions = [
    {
      name: "strengths",
      title: "What Are Your Strengths?",
      image: strengthImg,
      description:
        "Enter strengths that are internal to your company. You can control these and they give you a distinct advantage."
    },
    {
      name: "weaknesses",
      title: "What Are Your Weaknesses?",
      image: weaknessImg,
      description:
        "Enter the weaknesses that are internal to your company. You can control these."
    },
    {
      name: "opportunities",
      title: "What Are Your Opportunities?",
      image: opportunityImg,
      description: "Enter the opportunities that are external to your company."
    },
    {
      name: "threats",
      title: "What Are Your Threats?",
      image: threatImg,
      description: "Enter the threats that are external to your company."
    }
  ]

  swotAutocompleteOptions = {
    strengths: [
      { name: "People" },
      { name: "Culture" },
      { name: "Reputation" },
      { name: "Relationships" },
      { name: "Concept of Business" },
      { name: "Motivation" },
      { name: "Market Leader" },
      { name: "Innovative" },
      { name: "Quality" },
      { name: "Experience" },
      { name: "Expertise" },
      { name: "Outstanding Service" },
      { name: "Values" },
      { name: "Knowledge" },
      { name: "Partners" },
      { name: "Agility" },
      { name: "Talent" },
      { name: "Golden Rule" },
      { name: "Leadership" },
      { name: "Collaborative" },
      { name: "Creative" }
    ],
    weaknesses: [
      { name: "Systems" },
      { name: "Leadership Development" },
      { name: "Supply Issues" },
      { name: "Software" },
      { name: "Pay/Benefits" },
      { name: "Turnover" },
      { name: "Retention" },
      { name: "Communication" },
      { name: "Training, Development, Mentoring" },
      { name: "Accountability" },
      { name: "Burnout from Workload" },
      { name: "Sales" },
      { name: "Brand Clarity" },
      { name: "Inefficiencies" },
      { name: "Lack of Priorities" },
      { name: "Metrics" },
      { name: "Internal Cost" }
    ],
    opportunities: [
      { name: "Technology" },
      { name: "New Project Types" },
      { name: "Economy" },
      { name: "Hybrid Work" },
      { name: "Disruptions" },
      { name: "Shifting Workforce Dynamics" },
      { name: "Deliver Digitally" },
      { name: "Regulations" },
      { name: "Referrals" },
      { name: "Recession" },
      { name: "Industry Consolidation" }
    ],
    threats: [
      { name: "Economy" },
      { name: "Competition" },
      { name: "Industry Consolidation" },
      { name: "Too Many Choices" },
      { name: "Subscription Fatigue" },
      { name: "Shifting Workforce Dynamics" },
      { name: "Cyber Threats" },
      { name: "Supply Chain Disruptions" },
      { name: "Competitive Talent Market" },
      { name: "Regulations" },
      { name: "Acts of God" },
      { name: "Digital World" },
      { name: "Recession" }
    ]
  }
}

export const onboardingStore = new OnboardingStore()